import { Button, Stack } from '@evaneos/b2b-ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useTrackEventAmplitude } from '../../../shared/analytics';
import { AllTranslationKeys } from '../../../shared/intl/types';
import { OverloadedReason, WorkloadStatus } from '../types';
import { useGetAgentWorkload } from '../queries';
import { useSetAgentWorkloadMutation } from '../mutations';

import { AgentOverloadedReasonModal } from './AgentOverloadedReasonModal';

interface Props {
    onChoiceHandled?: () => void;
    onOverloadedStatusSelected?: () => void;
    askForReasonIfOverloaded?: boolean;
    overloadedButtonLabel?: AllTranslationKeys;
    underloadedButtonLabel?: AllTranslationKeys;
    goodButtonLabel?: AllTranslationKeys;
}

export const AgentWorkloadForm = ({
    onChoiceHandled = () => {},
    onOverloadedStatusSelected,
    askForReasonIfOverloaded = true,
    overloadedButtonLabel = 'agent_settings.workload_status.overloaded',
    underloadedButtonLabel = 'agent_settings.workload_status.underloaded',
    goodButtonLabel = 'agent_settings.workload_status.good',
}: Props) => {
    const { data: currentAgentWorkload } = useGetAgentWorkload();
    const { mutate: setAgentWorkload } = useSetAgentWorkloadMutation();
    const { trackEvent } = useTrackEventAmplitude();

    const [isAgentOverloadedReasonModalOpen, setIsAgentOverloadedReasonModalOpen] = useState(false);
    const [, setSelectedOverloadedReason] = useState<OverloadedReason | null>(null);

    function handleWorkloadStatusSelected(workloadStatus: WorkloadStatus): void {
        let overloadedReason = null;
        if (workloadStatus === WorkloadStatus.Overloaded) {
            if (onOverloadedStatusSelected) {
                onOverloadedStatusSelected();
                return;
            }

            if (askForReasonIfOverloaded) {
                setIsAgentOverloadedReasonModalOpen(true);
                return;
            } else {
                overloadedReason = currentAgentWorkload?.overloadedReason ?? OverloadedReason.Other;
            }
        }

        changeAgentWorkloadStatus(workloadStatus, overloadedReason);
    }

    function handleOverloadedReasonSelected(reason: OverloadedReason): void {
        setSelectedOverloadedReason(reason);
        setIsAgentOverloadedReasonModalOpen(false);

        changeAgentWorkloadStatus(WorkloadStatus.Overloaded, reason);
    }

    function changeAgentWorkloadStatus(
        workloadStatus: WorkloadStatus,
        overloadedReason: OverloadedReason | null,
    ): void {
        setAgentWorkload({
            status: workloadStatus,
            overloadedReason: overloadedReason,
            lastUpdatedAt: new Date(),
        });

        trackEvent((ampli, defaultProperties) => {
            ampli.workloadStatusClicked({
                ...defaultProperties,
                agent_workload_level: workloadStatus,
            });
        });

        onChoiceHandled();
    }

    return (
        <Stack direction="column" spacing={1} alignItems="flex-start" justifyContent="flex-start">
            <Button
                variant="outlined"
                onClick={() => handleWorkloadStatusSelected(WorkloadStatus.Overloaded)}
            >
                <FormattedMessage id={overloadedButtonLabel} />
            </Button>
            <Button
                variant="outlined"
                onClick={() => handleWorkloadStatusSelected(WorkloadStatus.Good)}
            >
                <FormattedMessage id={goodButtonLabel} />
            </Button>
            <Button
                variant="outlined"
                onClick={() => handleWorkloadStatusSelected(WorkloadStatus.Underloaded)}
            >
                <FormattedMessage id={underloadedButtonLabel} />
            </Button>
            <AgentOverloadedReasonModal
                isOpen={isAgentOverloadedReasonModalOpen}
                onReasonSelected={handleOverloadedReasonSelected}
            />
        </Stack>
    );
};
